import { distanceMiles, DISTANCE_KINDS, ROUTE_MATRIX_MAX_DESTINATIONS } from './constants';

/**
 * Driving distance (miles) from one point to many, via Google's Routes API (`RouteMatrix` in the
 * Maps JS API — the successor of the legacy Distance Matrix service).
 *
 * - Results are cached per origin→destination pair for the session, so re-selecting a staff,
 *   re-hovering a client or re-clustering on zoom never re-bills the same pair.
 * - Pairs already being fetched are shared: a staff click asks from both the connector lines and the
 *   selection bar at once, and the second caller waits on the first request instead of sending its own.
 * - Only a refusal (API not enabled on the key, key not allowed, no billing) switches driving distance
 *   off for the session. Any other failure (network drop, timeout, rate limit) falls back to
 *   straight-line miles for that call only; the pairs stay uncached and the next call retries them.
 * - A destination with no drivable route (island, bad geocode) resolves to null for that pair.
 */
const cache = new Map();
const pending = new Map();
let unavailable = false;

const REFUSED_PATTERN = /PERMISSION_DENIED|REQUEST_DENIED|API_KEY|not (been )?(used|enabled|authorized)|billing|\b403\b/i;
const isRefused = (err) => REFUSED_PATTERN.test(`${err?.code ?? ''} ${err?.status ?? ''} ${err?.message ?? err ?? ''}`);

const round = (n) => Number(n).toFixed(5);
const pairKey = (from, to) => `${round(from.lat)},${round(from.lng)}|${round(to.lat)},${round(to.lng)}`;
const METERS_PER_MILE = 1609.344;

const fetchBatch = async (origin, destinations) => {
    const { RouteMatrix } = await window.google.maps.importLibrary('routes');
    const response = await RouteMatrix.computeRouteMatrix({
        origins: [origin],
        destinations,
        travelMode: 'DRIVING',
        fields: ['distanceMeters', 'condition'],
    });
    const items = response?.matrix?.rows?.[0]?.items ?? [];
    return destinations.map((_, i) => {
        const meters = items[i]?.distanceMeters;
        return typeof meters === 'number' && items[i]?.condition !== 'ROUTE_NOT_FOUND' ? meters / METERS_PER_MILE : null;
    });
};

/** Driving miles (or null) per destination, aligned with `destinations`. */
export const drivingMiles = async (origin, destinations) => {
    if (unavailable || !window.google?.maps?.importLibrary || !origin || !destinations.length) {
        return destinations.map(() => null);
    }
    const missing = [
        ...new Map(
            destinations.filter((d) => !cache.has(pairKey(origin, d)) && !pending.has(pairKey(origin, d))).map((d) => [pairKey(origin, d), d])
        ).values(),
    ];
    const ownRequest = missing.length ? fetchMissing(origin, missing) : null;
    if (ownRequest) missing.forEach((d) => pending.set(pairKey(origin, d), ownRequest));
    // Wait for this call's request and for any other caller's request covering these pairs
    await Promise.all(new Set(destinations.map((d) => pending.get(pairKey(origin, d))).filter(Boolean)));
    return destinations.map((d) => cache.get(pairKey(origin, d)) ?? null);
};

const fetchMissing = async (origin, missing) => {
    try {
        for (let i = 0; i < missing.length && !unavailable; i += ROUTE_MATRIX_MAX_DESTINATIONS) {
            const batch = missing.slice(i, i + ROUTE_MATRIX_MAX_DESTINATIONS);
            try {
                const miles = await fetchBatch(origin, batch);
                batch.forEach((d, j) => cache.set(pairKey(origin, d), miles[j]));
            } catch (err) {
                if (isRefused(err)) unavailable = true;
                // eslint-disable-next-line no-console
                console.warn(
                    unavailable
                        ? 'Driving distance unavailable — showing straight-line miles instead.'
                        : 'Driving distance request failed — showing straight-line miles, will retry.',
                    err?.message || err
                );
                break;
            }
        }
    } finally {
        missing.forEach((d) => pending.delete(pairKey(origin, d)));
    }
};

/** Cached driving miles for one pair, without a request — undefined when not looked up yet. */
export const cachedDrivingMiles = (origin, destination) => cache.get(pairKey(origin, destination));

/**
 * The distance to show for a pair: driving when known, else straight-line.
 * `{ miles, kind }` — kind is DISTANCE_KINDS.DRIVING or DISTANCE_KINDS.STRAIGHT.
 */
export const bestDistance = (origin, destination, driving) =>
    typeof driving === 'number'
        ? { miles: driving, kind: DISTANCE_KINDS.DRIVING }
        : { miles: distanceMiles(origin, destination), kind: DISTANCE_KINDS.STRAIGHT };
